import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { GoogleOAuthProvider } from '@react-oauth/google';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';

// Cashper AI Assistant
import CashperAssistant from './components/chatbot/CashperAssistant';
import ScrollToTop from './components/ScrollToTop';
import SessionManager from './components/SessionManager';

// Lazy load all components
const EnhancedHomePage = lazy(() => import('./pages/EnhancedHomePage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const Login = lazy(() => import('./components/auth/Login'));
const ForgotPassword = lazy(() => import('./components/auth/ForgotPassword'));
const CreateAccount = lazy(() => import('./components/auth/CreateAccount'));
const OTPLogin = lazy(() => import('./components/auth/OTPLogin'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const Health_Insurence = lazy(() => import('./components/Health_Insurence'));
const Term_Insurance = lazy(() => import('./components/Term_Insurance'));
const Moter_Insurance = lazy(() => import('./components/Moter_Insurance'));
const SIP = lazy(() => import('./components/SIP'));
const Mutual_funds = lazy(() => import('./components/Mutual_funds'));
const Personal_tax_planning = lazy(() => import('./components/Personal_tax_planning'));
const Business_Tax_planning = lazy(() => import('./components/Business_Tax_planning'));
const Short_Term_Loan = lazy(() => import('./components/Short_Term_Loan'));
const Home_Loan = lazy(() => import('./components/Home_Loan'));
const Personal_loan = lazy(() => import('./components/Personal_loan'));
const Business_loan = lazy(() => import('./components/Business_loan'));
const Dashboard = lazy(() => import('./components/dashbord/Dashboard'));
const AdminPanel = lazy(() => import('./components/Admin pannel/AdminPanel'));

// Retail Services
const FileITR = lazy(() => import('./pages/services/FileITR'));
const ReviseITR = lazy(() => import('./pages/services/ReviseITR'));
const ReplyITRNotice = lazy(() => import('./pages/services/ReplyITRNotice'));
const ApplyIndividualPAN = lazy(() => import('./pages/services/ApplyIndividualPAN'));
const ApplyHUFPAN = lazy(() => import('./pages/services/ApplyHUFPAN'));
const WithdrawPF = lazy(() => import('./pages/services/WithdrawPF'));
const UpdateAadhaarPAN = lazy(() => import('./pages/services/UpdateAadhaarPAN'));
const OnlineTradingDemat = lazy(() => import('./pages/services/OnlineTradingDemat'));
const BankAccount = lazy(() => import('./pages/services/BankAccount'));
const FinancialPlanning = lazy(() => import('./pages/services/FinancialPlanning'));

// Corporate Services
const RegisterCompany = lazy(() => import('./pages/services/RegisterCompany'));
const ComplianceNewCompany = lazy(() => import('./pages/services/ComplianceNewCompany'));
const TaxAudit = lazy(() => import('./pages/services/TaxAudit'));
const LegalAdvice = lazy(() => import('./pages/services/LegalAdvice'));
const ProvidentFundServices = lazy(() => import('./pages/services/ProvidentFundServices'));
const TDSServices = lazy(() => import('./pages/services/TDSServices'));
const GSTServices = lazy(() => import('./pages/services/GSTServices'));
const PayrollServices = lazy(() => import('./pages/services/PayrollServices'));
const AccountingBookkeeping = lazy(() => import('./pages/services/AccountingBookkeeping'));

// Loading fallback component
const LoadingFallback = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    fontSize: '1.5rem',
    color: '#333'
  }}>
    <div>Loading...</div>
  </div>
);
function App() {
  const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || "your-google-client-id.apps.googleusercontent.com";
  
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <Router>
        <ScrollToTop />
        <SessionManager />
        <div className="App">
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/" element={<EnhancedHomePage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
            <Route path="/contactus" element={<Navigate to="/contact" replace />} />
              
              {/* Authentication Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/create-account" element={<CreateAccount />} />
              <Route path="/otp-login" element={<OTPLogin />} />
            
            {/* Dashboard Routes with nested paths */}
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/dashboard/overview" element={<Dashboard />} />
            <Route path="/dashboard/loans" element={<Dashboard />} />
            <Route path="/dashboard/insurance" element={<Dashboard />} />
            <Route path="/dashboard/investments" element={<Dashboard />} />
            <Route path="/dashboard/tax" element={<Dashboard />} />
            <Route path="/dashboard/retail-services" element={<Dashboard />} />
            <Route path="/dashboard/corporate-services" element={<Dashboard />} />
            <Route path="/dashboard/calculators" element={<Dashboard />} />
            <Route path="/dashboard/profile" element={<Dashboard />} />
            <Route path="/dashboard/profile/edit" element={<Dashboard />} />
            <Route path="/dashboard/settings" element={<Dashboard />} />
            <Route path="/dashboard/notifications" element={<Dashboard />} />
            <Route path="/dashboard/documents" element={<Dashboard />} />
            <Route path="/dashboard/support" element={<Dashboard />} />
            
            {/* Admin Panel Routes with nested paths */}
            <Route path="/admin" element={<AdminPanel />} />
            <Route path="/admin/dashboard" element={<AdminPanel />} />
            <Route path="/admin/users" element={<AdminPanel />} />
            <Route path="/admin/documents" element={<AdminPanel />} />
            <Route path="/admin/loans" element={<AdminPanel />} />
            <Route path="/admin/insurance" element={<AdminPanel />} />
            <Route path="/admin/investments" element={<AdminPanel />} />
            <Route path="/admin/taxplanning" element={<AdminPanel />} />
            <Route path="/admin/taxconsultations" element={<AdminPanel />} />
            <Route path="/admin/retailservices" element={<AdminPanel />} />
            <Route path="/admin/corporateservices" element={<AdminPanel />} />
            <Route path="/admin/reports" element={<AdminPanel />} />
            <Route path="/admin/settings" element={<AdminPanel />} />
            <Route path="/admin/profile" element={<AdminPanel />} />
            <Route path="/admin/help" element={<AdminPanel />} />
            <Route path="/admin/logs" element={<AdminPanel />} />
            <Route path="/admin/notifications" element={<AdminPanel />} />
            
            <Route path="/consultation" element={<ContactPage />} />
            {/* Insurance Routes */}
            <Route path="/insurance/health" element={<Health_Insurence />} />
            <Route path="/insurance/term" element={<Term_Insurance />} />
            <Route path="/insurance/motor" element={<Moter_Insurance />} />
            
            {/* Investment Routes */}
            <Route path="/investments/sip" element={<SIP />} />
            <Route path="/investments/mutual-funds" element={<Mutual_funds />} />
            
            {/* Tax Planning Routes */}
            <Route path="/tax-planning/personal" element={<Personal_tax_planning />} />
            <Route path="/services/tax-planning" element={<Personal_tax_planning />} />
            <Route path="/tax-planning/business" element={<Business_Tax_planning />} />
            
            {/* Loan Routes */}
            <Route path="/loans/short-term" element={<Short_Term_Loan />} />
            <Route path="/loans/home" element={<Home_Loan />} />
            <Route path="/loans/personal" element={<Personal_loan />} />
            <Route path="/loans/business" element={<Business_loan />} />
            {/* TODO: Add remaining loan routes when components are created:
            <Route path="/loans/loan-against-property" element={<Loan_Against_Property />} />
            <Route path="/loans/od-cc-limit" element={<OD_CC_Limit />} />
            <Route path="/loans/cgtmse-support" element={<CGTMSE_Support />} />
            */}
            
            {/* Retail Services Routes */}
            <Route path="/services/file-itr" element={<FileITR />} />
            <Route path="/services/revise-itr" element={<ReviseITR />} />
            <Route path="/services/reply-itr-notice" element={<ReplyITRNotice />} />
            <Route path="/services/apply-individual-pan" element={<ApplyIndividualPAN />} />
            <Route path="/services/apply-huf-pan" element={<ApplyHUFPAN />} />
            <Route path="/services/withdraw-pf" element={<WithdrawPF />} />
            <Route path="/services/update-aadhaar-pan" element={<UpdateAadhaarPAN />} />
            <Route path="/services/online-trading-demat" element={<OnlineTradingDemat />} />
            <Route path="/services/bank-account" element={<BankAccount />} />
            <Route path="/services/financial-planning" element={<FinancialPlanning />} />
            
            {/* Corporate Services Routes */}
            <Route path="/services/register-company" element={<RegisterCompany />} />
            <Route path="/services/compliance-new-company" element={<ComplianceNewCompany />} />
            <Route path="/services/tax-audit" element={<TaxAudit />} />
            <Route path="/services/legal-advice" element={<LegalAdvice />} />
            <Route path="/services/provident-fund-services" element={<ProvidentFundServices />} />
            <Route path="/services/tds-services" element={<TDSServices />} />
            <Route path="/services/gst-services" element={<GSTServices />} />
            <Route path="/services/payroll-services" element={<PayrollServices />} />
            <Route path="/services/accounting-bookkeeping" element={<AccountingBookkeeping />} />
            
            {/* Calculator Routes - These routes redirect to dashboard calculators section */}
            <Route path="/calculators/*" element={<Dashboard />} />
            
            {/* Legal Routes */}
            <Route path="/legal/terms" element={<TermsOfService />} />
            <Route path="/legal/privacy" element={<PrivacyPolicy />} />
            <Route path="/legal/cookies" element={<CookiePolicy />} />

            {/* Catch-all: unknown paths redirect home instead of blanking the app */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        {/* Cashper AI Assistant — appears on all pages */}
        <CashperAssistant />
      </div>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </Router>
    </GoogleOAuthProvider>
  );
}
export default App;


